import { useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import { Separator } from '@components/ApparelProductSkeleton/styles'
import { DesktopSuggestedProducts } from '@components/CartDrawer/DesktopSuggestedProducts/DesktopSuggestedProducts'
import { EmptyCart } from '@components/CartDrawer/EmptyCart/EmptyCart'
import { ErrorHandler } from '@components/CartDrawer/ErrorHandler/ErrorHandler'
import { Footer } from '@components/CartDrawer/Footer/Footer'
import { LineItem } from '@components/CartDrawer/LineItem/LineItem'
import { LineItemSkeleton } from '@components/CartDrawer/LineItem/LineItemSkeleton'
import { MobileSuggestedProducts } from '@components/CartDrawer/MobileSuggestedProducts/MobileSuggestedProducts'
import { Summary } from '@components/CartDrawer/Summary/Summary'
import type { CheckedState } from '@radix-ui/react-checkbox'
import { dataTestIds } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { Drawer } from '@velasca/ui/drawer'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { UnstyledList } from '@velasca/ui/unstyled-list'
import { useExchangeRates } from '../ExchangeRatesProvider'
import { CartListing } from './CartDrawer.styles'
import type { CartDrawerProps } from './CartDrawer.types'
import { Reassurance } from './Reassurance/Reassurance'

export const CartDrawer = ({
  emptyCartCtas,
  editorialSuggestedProductsDesktopImage,
}: CartDrawerProps): JSX.Element | null => {
  const { isDrawerOpen, setIsDrawerOpen } = useCart()
  const {
    loading: cartLoading,
    data: cartData,
    setError: setCartError,
  } = useCart()

  const [isGiftOrder, setIsGiftOrder] = useState<CheckedState>(false)

  const { t } = useTranslation('cartDrawer')

  const { convert } = useExchangeRates()

  const isCartPopulated = Boolean(
    (!cartData && cartLoading) || (cartData && !isEmpty(cartData.items)),
  )

  return (
    <Drawer
      isOpen={isDrawerOpen}
      sideChildren={
        <DesktopSuggestedProducts
          editorialImage={editorialSuggestedProductsDesktopImage}
          isCartPopulated={isCartPopulated}
        />
      }
      close={() => {
        setIsDrawerOpen(false)
      }}
    >
      <Drawer.Header
        title={t('cart_drawer_title')}
        onClose={() => {
          setIsDrawerOpen(false)
        }}
      />
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => {
          setCartError(null)
        }}
      >
        <ErrorHandler>
          <Drawer.Content isLoading={cartLoading}>
            {isCartPopulated ? (
              <>
                <CartListing>
                  <UnstyledList data-testid={dataTestIds.CART_PRODUCT_LIST}>
                    {!cartData && cartLoading
                      ? [1, 2, 3].map((e) => <LineItemSkeleton key={e} />)
                      : cartData?.items.map(
                          ({
                            id,
                            handle,
                            image,
                            product_title,
                            price,
                            compareAtPrice,
                            product_type,
                            quantity,
                            options,
                          }) => {
                            return (
                              <li key={id}>
                                <LineItem
                                  productID={id}
                                  handle={handle}
                                  image={image}
                                  title={product_title || ''}
                                  productType={product_type}
                                  initialItemQuantity={quantity}
                                  color={options?.color}
                                  size={options?.size}
                                  rawPrice={price}
                                  material={options?.material}
                                  imgAlt={t(
                                    'cart_drawer_product_alt_template',
                                    {
                                      color: options?.color,
                                      material: options?.material,
                                      productTitle: product_title || '',
                                      productType: product_type,
                                    },
                                  )}
                                  // Compare at price needs * 100 because it's coming from the GraphQL call, while price is coming from cart.js call
                                  rawCompareAtPrice={
                                    (compareAtPrice?.max ?? 0) * 100
                                  }
                                />
                                <Separator />
                              </li>
                            )
                          },
                        )}
                  </UnstyledList>

                  {cartData?.total_price ? (
                    <Summary
                      setIsGiftOrder={setIsGiftOrder}
                      cartTotalPrice={convert(cartData.total_price)}
                    />
                  ) : null}
                </CartListing>
                <MobileSuggestedProducts />
                <Reassurance />
              </>
            ) : (
              <EmptyCart emptyCartCtas={emptyCartCtas} />
            )}
          </Drawer.Content>
        </ErrorHandler>
        {cartData && !isEmpty(cartData.items) && (
          <form noValidate action="/cart" method="POST">
            <Footer cartTotalValue={cartData?.total_price} />
            <input
              type="hidden"
              value={isGiftOrder ? 'T' : 'F'}
              name="attributes[is_gift_order]"
            />
          </form>
        )}
      </ErrorBoundary>
    </Drawer>
  )
}
